import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ImageOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const Chapter = ({ n }) => <span className="font-serif-display text-sm italic text-[var(--gold)]">{n}</span>;

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gallery`)
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <section id="gallery" className="bg-[var(--cream-2)] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <Reveal>
          <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[var(--gold)]">
            <Chapter n="06" /> — Gallery
          </p>
          <h2 className="max-w-2xl font-serif-display text-4xl leading-tight tracking-tight text-[var(--ink)] md:text-5xl">
            A closer look at Nova Royale.
          </h2>
        </Reveal>

        {!loaded ? (
          <p className="mt-12 text-[var(--ink-soft)]">Loading gallery…</p>
        ) : items.length === 0 ? (
          <Reveal className="mt-12">
            <div className="flex flex-col items-center justify-center rounded-sm border border-dashed border-[var(--cream-3)] bg-white/60 py-24 text-center" data-testid="gallery-empty">
              <ImageOff className="mb-4 text-[var(--gold)]" size={34} />
              <p className="font-serif-display text-2xl text-[var(--ink)]">Photos coming soon</p>
              <p className="mt-2 max-w-sm text-sm text-[var(--ink-soft)]">
                Our team is adding photographs of the site, roads and amenities. Please check back shortly.
              </p>
            </div>
          </Reveal>
        ) : (
          <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-3" data-testid="gallery-grid">
            {items.map((img, i) => (
              <Reveal key={img.id} delay={(i % 3) * 0.08} className="mb-5 break-inside-avoid">
                <figure className="group overflow-hidden rounded-sm border border-[var(--cream-3)] bg-white" data-testid={`gallery-item-${img.id}`}>
                  <div className="overflow-hidden">
                    <img
                      src={`${API}/gallery/${img.id}/file`}
                      alt={img.caption || "Nova Royale"}
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  {img.caption ? (
                    <figcaption className="flex items-center gap-2 px-5 py-4 text-sm text-[var(--ink-soft)]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]" />
                      {img.caption}
                    </figcaption>
                  ) : null}
                </figure>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
